// The calendar as a spreadsheet.
//
// Its own module for the reason `scheduleCsv.ts` is one: both views export the
// same rows, and the two must produce identical columns or a plan made from one
// would not match a plan made from the other.
//
// SEVEN KINDS, ONE TABLE, which forces a decision the other export does not
// face: a bill's gross, a recharge's partner share and a suggested
// implementation fee are three different meanings of "amount". They share a
// column anyway, with `kind` beside it saying which — because the alternative is
// seven amount columns, six of them empty on every row, and a spreadsheet nobody
// can sort. `amount_minor` is therefore the figure the SCREEN shows for that
// event, and summing the column across kinds is meaningless on purpose: the
// per-kind totals are what the screen states, and they are what reconciles.

import type { CalendarEvent, Currency } from "@crm/shared";
import { describeCalendarEvent } from "./calendarEvents.js";

/**
 * Exactly the events on screen — the same window, the same kind filter.
 *
 * Minor units and the ISO date rather than the formatted cells, because a
 * spreadsheet wants a number it can sum and a date it can sort, not
 * "$1,250.00" and "Sep 1, 2026". The `summary` column is the one exception: it
 * is the screen's own sentence, and it is what makes a row about a term change
 * legible at all.
 */
export function calendarCsv(events: readonly CalendarEvent[], currency: Currency): string {
  const header = [
    "date",
    "kind",
    "who",
    "summary",
    "currency",
    "amount_minor",
    "partner",
    "partner_id",
    "deal_id",
    "agreement_id",
  ];

  const body = events.map((event) => {
    const text = describeCalendarEvent(event, currency);
    const amountOf = amountMinorOf(event);
    return [
      event.date,
      event.kind,
      text.headline,
      text.summary,
      currency,
      amountOf === null ? "" : String(amountOf),
      event.partner_name,
      event.partner_id,
      event.deal_id ?? "",
      event.agreement_id ?? "",
    ];
  });

  return [header, ...body].map((cells) => cells.map(escapeCsv).join(",")).join("\r\n");
}

/**
 * The one figure this event is about, in minor units — the same one the chip
 * shows.
 *
 * NULL, WRITTEN AS AN EMPTY CELL, wherever there is no figure: a term change,
 * which carries no money at all; an implementation fee nobody can price yet; and
 * a bill or a cost recovery that is certain and still waiting on its
 * number. Those rows belong in the export precisely BECAUSE they have no figure,
 * and a 0 would put them in a spreadsheet's SUM as though they were free. An
 * empty cell sums to the same thing a missing row would and reads as what it is.
 */
function amountMinorOf(event: CalendarEvent): number | null {
  switch (event.kind) {
    case "bill_due":
      return event.gross_minor;
    case "cost_bill_due":
      // What goes on the bill, never our absorbed share — the same figure the
      // screen shows, for the same reason.
      return event.partner_share_minor;
    case "impl_fee_due":
      return event.suggested_gross_minor;
    case "term_line_change":
      return event.amount_minor ?? event.rate_minor;
    case "deal_start":
    case "deal_renewal":
      return event.contracted_gross_minor;
    case "term_change":
      return null;
  }
}

/** A customer name with a comma in it must not become two columns. */
function escapeCsv(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}
